/**
 * ALTIORA · Feedback Loop · Post-sale mini-questionnaire.
 *
 * Mounted globally by AppShell. It polls /feedback_loop/pending every 90s
 * and pops a modal for the first pending won deal. The advisor can either
 * fill it in (which earns +30 XP) or dismiss it (deferred to next poll).
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Modal, FormField, Textarea, PrimaryButton, GhostButton, Label,
} from "@/components/altiora/PremiumUI";
import { Star } from "lucide-react";


const dismissedKey = "altiora_fb_dismissed";
const dismissedSet = () => {
  try { return new Set(JSON.parse(sessionStorage.getItem(dismissedKey) || "[]")); }
  catch { return new Set(); }
};

export function FeedbackAutoPrompt() {
  const [pending, setPending] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({
    rating_overall: 4, rating_fit: 4,
    what_worked: "", what_blocked: "", lessons: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/feedback_loop/pending");
      setPending(data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 90_000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (active || pending.length === 0) return;
    const dismissed = dismissedSet();
    const next = pending.find(p => !dismissed.has(p.deal_id));
    if (next) setActive(next);
  }, [pending, active]);

  const close = (persist = false) => {
    if (persist && active?.deal_id) {
      const d = dismissedSet();
      d.add(active.deal_id);
      sessionStorage.setItem(dismissedKey, JSON.stringify([...d]));
    }
    setActive(null);
    setForm({ rating_overall: 4, rating_fit: 4,
                what_worked: "", what_blocked: "", lessons: "" });
  };

  const submit = async () => {
    if (!active) return;
    setSubmitting(true);
    try {
      await api.post("/feedback_loop/submit", {
        deal_id: active.deal_id,
        ...form,
      });
      toast.success("Merci pour votre retour · +30 XP");
      close(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Envoi impossible");
    } finally { setSubmitting(false); }
  };

  if (!active) return null;
  return (
    <Modal open onClose={() => close(true)}
           testid="feedback-prompt-modal"
           title={`Retour rapide · ${active.client_name}`}
           description="Aidez l'IA à mieux vous coacher : deux minutes suffisent."
           footer={
             <>
               <GhostButton onClick={() => close(true)}
                            testid="feedback-prompt-later">Plus tard</GhostButton>
               <PrimaryButton onClick={submit} disabled={submitting}
                              testid="feedback-prompt-submit">
                 {submitting ? "Envoi…" : "Enregistrer · +30 XP"}
               </PrimaryButton>
             </>
           }>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <RatingRow label="Satisfaction globale" value={form.rating_overall}
                   onChange={(v) => setForm({ ...form, rating_overall: v })}
                   testid="feedback-rating-overall" />
        <RatingRow label="Adéquation produit" value={form.rating_fit}
                   onChange={(v) => setForm({ ...form, rating_fit: v })}
                   testid="feedback-rating-fit" />
      </div>
      <FormField label="Ce qui a bien fonctionné" testid="feedback-worked">
        {({ id }) => (
          <Textarea id={id} rows={2} value={form.what_worked}
                    onChange={(e) => setForm({ ...form, what_worked: e.target.value })}
                    testid="feedback-worked-input" />
        )}
      </FormField>
      <FormField label="Le principal frein" testid="feedback-blocked">
        {({ id }) => (
          <Textarea id={id} rows={2} value={form.what_blocked}
                    onChange={(e) => setForm({ ...form, what_blocked: e.target.value })}
                    testid="feedback-blocked-input" />
        )}
      </FormField>
      <FormField label="Ma leçon retenue" testid="feedback-lessons">
        {({ id }) => (
          <Textarea id={id} rows={2} value={form.lessons}
                    onChange={(e) => setForm({ ...form, lessons: e.target.value })}
                    testid="feedback-lessons-input" />
        )}
      </FormField>
    </Modal>
  );
}


function RatingRow({ label, value, onChange, testid }) {
  return (
    <div>
      <Label testid={`${testid}-label`}>{label}</Label>
      <div className="flex gap-1 mt-1" data-testid={testid}>
        {[1, 2, 3, 4, 5].map(v => (
          <button key={v} onClick={() => onChange(v)}
                  data-testid={`${testid}-${v}`}
                  aria-label={`${v} sur 5`}
                  className="p-1">
            <Star size={20}
                  fill={v <= value ? "#F5D687" : "none"}
                  stroke={v <= value ? "#C5A880" : "#94A3B8"} />
          </button>
        ))}
      </div>
    </div>
  );
}
